import { Component, OnDestroy, OnInit, inject } from '@angular/core';
import {
  UntypedFormBuilder,
  UntypedFormGroup,
  Validators,
} from '@angular/forms';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import {
  IBeacon,
  ICliente,
  IConfigVecino,
  ICreateBeacon,
  IFilter,
  IListado,
  IQueryParam,
  ISirena,
  // ISocketMessage,
} from 'modelos/src';
import { firstValueFrom, Observable, of, Subject, Subscription } from 'rxjs';
import { debounceTime, distinctUntilChanged, switchMap } from 'rxjs/operators';
import { HelperService } from '../../../auxiliares/servicios/helper.service';
import { ListadosService } from '../../../auxiliares/servicios/listados.service';
import { WebSocketService } from '../../../auxiliares/servicios/websocket';
import { ControlesBTService } from '../controles-bt.service';

@Component({
  selector: 'app-crear-editar-controles-bt',
  templateUrl: './crear-editar-controles-bt.component.html',
  styleUrls: ['./crear-editar-controles-bt.component.scss'],
  standalone: false,
})
export class CrearEditarControlesBTComponent implements OnInit, OnDestroy {
  data = inject<IBeacon>(MAT_DIALOG_DATA);
  private fb = inject(UntypedFormBuilder);
  private dialogRef =
    inject<MatDialogRef<CrearEditarControlesBTComponent>>(MatDialogRef);
  private service = inject(ControlesBTService);
  private helper = inject(HelperService);
  private listadosService = inject(ListadosService);
  private webSocketService = inject(WebSocketService);

  formulario?: UntypedFormGroup;
  loading = false;
  title?: string;
  hide = true;

  clientes: ICliente[] = [];

  configs: IConfigVecino[] = [];
  configs$?: Observable<IConfigVecino[]>;
  inputConfigs$ = new Subject<string>();

  sirenas: ISirena[] = [];
  sirenas$?: Observable<ISirena[]>;
  inputSirenas$ = new Subject<string>();

  private clientes$?: Subscription;
  private wsSubscription?: Subscription;

  private crearFormulario(): void {
    this.title = this.data?._id ? 'Editar Control' : 'Crear Control';
    this.formulario = this.fb.group({
      etiqueta: [this.data?.etiqueta],
      mac: [this.data?.mac, [Validators.required, Validators.minLength(4)]],
      idCliente: [this.data?.idCliente],
      idConfigVecino: [this.data?.idConfigVecino],
      incrementar: [false],
    });

    if (this.data?.configVecino) {
      this.configs = [this.data.configVecino];
      this.configs$ = of([this.data.configVecino]);
    }
  }

  public close(): void {
    this.dialogRef.close();
  }

  private getData(): ICreateBeacon {
    const data: ICreateBeacon = {
      mac: this.formulario?.get('mac')?.value,
      idCliente: this.formulario?.get('idCliente')?.value,
      idConfigVecino: this.formulario?.get('idConfigVecino')?.value,
      etiqueta: this.formulario?.get('etiqueta')?.value?.trim() || undefined,
      baneado: false,
    };
    return data;
  }

  public async enviar(): Promise<void> {
    this.loading = true;
    try {
      const data = this.getData();
      console.log('data', data);
      if (this.data) {
        await firstValueFrom(this.service.editar(this.data._id!, data));
        this.helper.notifSuccess('Editado correctamente');
        this.dialogRef.close(true);
      } else {
        await firstValueFrom(this.service.crear(data));
        this.helper.notifSuccess('Creado correctamente');
        //
        this.formulario?.patchValue({ mac: null });
        this.formulario?.patchValue({ idConfigVecino: null });
        // Incremento de etiqueta para cargar controles en serie
        const incrementar = this.formulario?.get('incrementar')?.value;
        const siguiente = HelperService.siguienteEtiqueta(data.etiqueta);
        if (incrementar && siguiente) {
          this.formulario?.patchValue({ etiqueta: siguiente });
        } else {
          if (incrementar && data.etiqueta) {
            this.helper.notifWarn(
              `La etiqueta "${data.etiqueta}" no termina en número, no se pudo incrementar`,
            );
          }
          this.formulario?.patchValue({ etiqueta: null });
        }
        //
      }
    } catch (error) {
      console.error(error);
      this.helper.notifError(error);
    }
    this.loading = false;
  }

  // Listados

  private async listarClientes(): Promise<void> {
    const query: IQueryParam = {
      sort: 'nombre',
      select: 'nombre',
    };

    this.clientes$?.unsubscribe();
    this.clientes$ = this.listadosService
      .subscribe<IListado<ICliente>>('clientes', query)
      .subscribe((data) => {
        this.clientes = data.datos;
        console.debug(new Date().toLocaleString(), `listado de clientes`, data);
      });
    await this.listadosService.getLastValue('clientes', query);
  }

  private crearFiltroConfig(search: string): IQueryParam {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const filtro: IFilter<any> = {};
    if (search) {
      filtro.$or = [
        { 'datosPersonales.dni': { $regex: search, $options: 'i' } },
        { 'datosPersonales.nombre': { $regex: search, $options: 'i' } },
      ];
    }
    const idCliente = this.formulario?.get('idCliente')?.value;
    if (idCliente) {
      filtro.idCliente = idCliente;
    }

    const populate = {
      path: 'cliente',
      select: 'nombre',
    };
    const query: IQueryParam = {
      filter: JSON.stringify(filtro),
      limit: 10,
      populate: JSON.stringify(populate),
      select: 'datosPersonales idCliente cliente',
    };
    return query;
  }

  onSearchConfigs() {
    this.configs$ = of([]);
    this.inputConfigs$
      .pipe(
        debounceTime(200),
        distinctUntilChanged(),
        switchMap((term) =>
          this.listadosService.listarConfigVecinos$(
            this.crearFiltroConfig(term),
          ),
        ),
      )
      .subscribe((data) => {
        console.log('configs encontradas', data);
        this.configs = data;
        this.configs$ = of(data);
      });
  }

  async ngOnInit(): Promise<void> {
    this.loading = true;
    this.crearFormulario();
    await this.listarClientes();
    this.onSearchConfigs();
    console.log('this.data', this.data);

    this.loading = false;
  }

  ngOnDestroy(): void {
    this.clientes$?.unsubscribe();
    this.wsSubscription?.unsubscribe();
  }
}
